import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { OperatorDetails } from '../models/OperatorDetails';
import { NetworkContract } from '../models/NetworkContract';
import { OperatorPlaceDetails } from "../models/OperatorPlaceDetails";
import { PlaceFilterForm } from "../models/PlaceFilterForm";
import { PlaceDetailsElectricity } from "../../account/models/PlaceDetailsElectricity";
import { PlaceDetailsGas } from "../../account/models/PlaceDetailsGas";
import { Page } from '../../../shared/model/page';
import { Invitation } from '../../../shared/components/invitation-options-component/invitation';
import { Notification } from '../../account/models/Notification';
import { NetworkContractFilterForm } from "../models/NetworkContractFilterForm";
import { ConventionFilter } from "../models/ConventionFilter";
import { PlaceConvention } from "../models/PlaceConvention";
import { NotificationFilterForm } from '../../anre/notifications-anre/models/NotificationFilterForm';
import { Place } from '../../account/models/Place';

@Injectable({
  providedIn: 'root'
})
export class OperatorService {

  private operatorUrl = '/operator';

  constructor(private http: HttpClient) {
  }

  getElectricityNetworkContract(id: number): Observable<NetworkContract> {
    return this.http.get<NetworkContract>(environment.baseUrl + this.operatorUrl + `/detalii-contract-de-retea/electricitate/${id}`);
  }

  getGasNetworkContract(networkContractId: number): Observable<NetworkContract> {
    return this.http.get<NetworkContract>(environment.baseUrl + this.operatorUrl + `/detalii-contract-de-retea/gaze/${networkContractId}`);
  }

  getOPElectricityNetworkContracts(networkContractFilterForm: NetworkContractFilterForm): Observable<Page<NetworkContract>> {
    return this.http.post<Page<NetworkContract>>(environment.baseUrl + this.operatorUrl + '/lista-contracte/electricitate', networkContractFilterForm);
  }

  getOPGasNetworkContracts(networkContractFilterForm: NetworkContractFilterForm): Observable<Page<NetworkContract>> {
    return this.http.post<Page<NetworkContract>>(environment.baseUrl + this.operatorUrl + '/lista-contracte/gaze', networkContractFilterForm);
  }

  getOPElectricityConsumptionPlaces(placeFilterForm: PlaceFilterForm): Observable<Page<OperatorPlaceDetails>> {
    return this.http.post<Page<OperatorPlaceDetails>>(environment.baseUrl + '/operator/energie-electrica/locuri-de-consum', placeFilterForm);
  }

  getOPGasConsumptionPlaces(placeFilterForm: PlaceFilterForm): Observable<Page<OperatorPlaceDetails>> {
    return this.http.post<Page<OperatorPlaceDetails>>(environment.baseUrl + '/operator/gaz/locuri-de-consum', placeFilterForm);
  }

  getOperatorDetails(): Observable<OperatorDetails> {
    return this.http.get<OperatorDetails>(environment.baseUrl + this.operatorUrl + '/detalii-operator');
  }

  getOperatorStatus(): Observable<any> {
    return this.http.get<any>(environment.baseUrl + this.operatorUrl + '/status-operator');
  }

  updateOperatorDetails(operatorDetailsDTO: OperatorDetails): Observable<OperatorDetails> {
    return this.http.put<OperatorDetails>(environment.baseUrl + this.operatorUrl + '/detalii-operator', operatorDetailsDTO);
  }

  updateOperatorDataDetails(formData: FormData): Observable<OperatorDetails> {
    return this.http.put<OperatorDetails>(environment.baseUrl + this.operatorUrl + '/detalii-operator', formData);
  }

  getOperatorNotifications(notificationFilterForm: NotificationFilterForm): Observable<Page<any>> {
    return this.http.post<Page<any>>(environment.baseUrl + this.operatorUrl + '/notifications', notificationFilterForm);
  }

  getOPElectricitySuppliers(): Observable<any[]> {
    return this.http.get<any[]>(environment.baseUrl + '/operator/lista-furnizori/electricitate');
  }

  getOPGasSuppliers(): Observable<any[]> {
    return this.http.get<any[]>(environment.baseUrl + '/operator/lista-furnizori/gas');
  }

  cancelSupplier(requestObject: any): Observable<void> {
    return this.http.put<void>(environment.baseUrl + this.operatorUrl + '/furnizor/imposibilitate', requestObject);
  }

  inactivateCancelSupplier(series: string): Observable<void> {
    return this.http.put<void>(environment.baseUrl + this.operatorUrl + `/furnizor/anulare-imposibilitate?series=${series}`, {});
  }

  inactivateStatusConsumptionPlace(placeCode: string): Observable<void> {
    return this.http.put<void>(environment.baseUrl + this.operatorUrl + `/furnizor/inactiveaza-status-loc-consum?placeCode=${placeCode}`, {});
  }

  getOperatorNameConsumptionPlace(): Observable<PlaceDetailsElectricity> {
    return this.http.get<PlaceDetailsElectricity>(environment.baseUrl + '/operator/nume-operator-loc-consum-electricitate');
  }

  createElectricityConsumptionPlace(consumptionPlace: PlaceDetailsElectricity, actionType: string): Observable<PlaceDetailsElectricity> {
    return this.http.post<PlaceDetailsElectricity>(environment.baseUrl + `/operator/energie-electrica/creare-loc-consum/${actionType}`, consumptionPlace);
  }

  createGasConsumptionPlace(consumptionPlace: PlaceDetailsGas, actionType: string): Observable<PlaceDetailsGas> {
    return this.http.post<PlaceDetailsGas>(environment.baseUrl + `/operator/gas/creare-loc-consum/${actionType}`, consumptionPlace);
  }

  getInvitations(): Observable<Invitation[]> {
    return this.http.get<Invitation[]>(environment.baseUrl + '/invitatie/operator');
  }

  removeInvitation(invitationId: number): Observable<Invitation | null> {
    return this.http.delete<Invitation | null>(environment.baseUrl + `/invitatie/operator/${invitationId}`);
  }

  addInvitation(invitation: Invitation): Observable<Invitation> {
    return this.http.post<Invitation>(environment.baseUrl + '/invitatie/operator', invitation);
  }

  createElectricityNetworkContract(networkContract: NetworkContract): Observable<NetworkContract> {
    return this.http.post<NetworkContract>(environment.baseUrl + '/operator/creare-contract-de-retea/electricitate', networkContract);
  }

  createConventionForNetworkContract(placeConvention: PlaceConvention): Observable<PlaceConvention> {
    return this.http.post<PlaceConvention>(environment.baseUrl + '/operator/creare-conventie-contract-de-retea', placeConvention);
  }

  createGasNetworkContract(networkContract: NetworkContract): Observable<NetworkContract> {
    return this.http.post<NetworkContract>(environment.baseUrl + '/operator/creare-contract-de-retea/gaze', networkContract);
  }

  getPlacesFromContracts(placeFilterForm: PlaceFilterForm): Observable<Page<Place>> {
    return this.http.post<Page<Place>>(environment.baseUrl + '/operator/lista-locuri-de-consum', placeFilterForm);
  }

  updateNotificationRead(notification: Notification): Observable<Notification> {
    return this.http.put<Notification>(environment.baseUrl + this.operatorUrl + '/notification-status-update', notification);
  }

  getConventionParts(filter: ConventionFilter): Observable<any[]> {
    return this.http.post<any[]>(environment.baseUrl + this.operatorUrl + '/parti-conventie', filter);
  }

}
